/**
 * UniversityAutocomplete — debounced typeahead against the existing
 * /api/universities?search= endpoint. Keeps its own local search
 * string but reports the CANONICAL id + label to the parent via
 * `onSelect`. Used by both TalentProfile (student) and
 * TalentSearchTab (firm filter).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";


export default function UniversityAutocomplete({
  value,      // { id, name } | null
  onSelect,   // (uni | null) => void
  placeholder = "e.g. University of Oxford",
  testIdPrefix = "uni-ac",
  disabled = false,
}) {
  const [q, setQ] = useState(value?.name || "");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setQ(value?.name || "");
  }, [value?.id]); // eslint-disable-line

  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const search = (text) => {
    clearTimeout(debounceRef.current);
    if (!text || text.trim().length < 2) {
      setRows([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/universities?search=${encodeURIComponent(text.trim())}&limit=8&view=card`,
        );
        setRows(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch {
        setRows([]);
      }
    }, 220);
  };

  const clear = () => {
    setQ("");
    setRows([]);
    setOpen(false);
    onSelect?.(null);
  };

  return (
    <div className="relative" ref={containerRef}>
      <Input
        value={q}
        onChange={(e) => {
          const v = e.target.value;
          setQ(v);
          if (value?.id) onSelect?.(null);
          search(v);
        }}
        onFocus={() => q.length >= 2 && setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={`${testIdPrefix}-input`}
      />
      {value?.id && (
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-stone-100"
          onClick={clear}
          data-testid={`${testIdPrefix}-clear`}
          aria-label="Clear university"
        >
          <X className="h-3.5 w-3.5 text-stone-500" />
        </button>
      )}
      {open && rows.length > 0 && (
        <ul
          className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-stone-200 bg-white shadow-lg"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {rows.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onClick={() => {
                  onSelect?.({ id: u.id, name: u.name });
                  setQ(u.name);
                  setOpen(false);
                }}
                data-testid={`${testIdPrefix}-option-${u.id}`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50 flex items-center justify-between gap-2"
              >
                <span className="truncate">{u.name}</span>
                <span className="text-[10px] font-mono text-stone-400 uppercase shrink-0">
                  {u.country}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
